import {
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { getDatabaseAsync } from "configuration";

// SEC-X2: All write operations previously in this file (saveUserDoc,
// updateUserDoc, updateTheme, updateEmailNotifications, updateSettings)
// have been removed. They were writing directly to Firestore from the
// client, bypassing API validation and sanitisation. Writes now go
// through one-auth-api endpoints. Only reads remain here.

export interface UserData {
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

export const userService = {
  /**
   * Read a user document from Firestore.
   * Used by profile and settings pages to populate form state.
   */
  async getUserDoc(uid: string): Promise<(UserData & { id: string }) | null> {
    const db = await getDatabaseAsync();
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...(snap.data() as UserData) } : null;
  },

  /**
   * Download the authenticated user's data via the API.
   * Correctly calls GET /user/export — no direct Firestore access.
   */
  async exportUserData(): Promise<ExportResult> {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/user/export`, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(errText || "Failed to export data");
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/i);
    const filename = m?.[1] || `export-${Date.now()}.json`;
    return { blob, filename };
  },

  /**
   * Subscribe to a user document in real-time.
   * Returns an unsubscribe function — caller must invoke on unmount.
   */
  onUserDocSnapshot(
    uid: string,
    callback: (doc: (UserData & { id: string }) | null) => void,
  ): () => void {
    if (!uid) return () => {};

    let cancelled = false;
    let realUnsub = () => {};

    getDatabaseAsync().then((db) => {
      if (cancelled) return;
      const ref = doc(db, "users", uid);
      realUnsub = onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists() ? { id: snap.id, ...(snap.data() as UserData) } : null;
          callback(data);
        },
        (error) => console.error("Snapshot listener error:", error),
      );
    });

    return () => {
      cancelled = true;
      realUnsub();
    };
  },
};
